import React from 'react';
import { PropTypes } from 'prop-types';
import { Badge, Button, Skeleton, Table } from '@mantine/core';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { currencyFormat, formatUtcDate } from '../../../helpers/format';
import { getInvoiceSummary } from '../../../helpers/customerPaymentsHelper';

const CELL_STYLE = { padding: '10px 12px' };
const AMOUNT_CELL_STYLE = { padding: '10px 12px', textAlign: 'end' };
const TRUNCATE_STYLE = { whiteSpace: 'nowrap', maxWidth: 320 };
const SUB_AMOUNT_STYLE = {
  fontSize: 12,
  color: '#666',
  whiteSpace: 'nowrap'
};
const STRUCK_AMOUNT_STYLE = {
  fontSize: 12,
  color: '#666',
  whiteSpace: 'nowrap',
  textDecoration: 'line-through'
};
const DUE_AMOUNT_STYLE = {
  fontSize: 14,
  color: '#C40000',
  whiteSpace: 'nowrap'
};

const CustomerInvoiceListItem = ({ invoice, onViewPayments }) => {
  const summary = invoice ? getInvoiceSummary(invoice) : null;
  const primaryLabel =
    invoice?.participantName ||
    invoice?.description ||
    invoice?.associationName ||
    'Invoice';
  const secondaryLabel =
    (invoice?.description !== primaryLabel ? invoice?.description : null) ||
    invoice?.associationName ||
    null;

  return invoice ? (
    <Table.Tr
      style={{
        backgroundColor: invoice.isOutstanding
          ? 'rgba(196, 0, 0, 0.07)'
          : undefined
      }}
    >
      <Table.Td style={CELL_STYLE}>
        <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
          {invoice.logoImageUrl && (
            <img
              alt={invoice.associationName ?? 'Association'}
              height={28}
              src={invoice.logoImageUrl}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              width={28}
            />
          )}
          <AppStack style={{ gap: 0, minWidth: 0 }}>
            <AppText
              style={{ fontSize: 14, ...TRUNCATE_STYLE }}
              title={primaryLabel}
              weight={500}
            >
              {primaryLabel}
            </AppText>
            {secondaryLabel && (
              <AppText
                style={{ fontSize: 12, color: '#666', ...TRUNCATE_STYLE }}
                title={secondaryLabel}
              >
                {secondaryLabel}
              </AppText>
            )}
            {invoice.pkRegCart != null && (
              <AppText
                component={Link}
                style={{
                  fontSize: 12,
                  textDecoration: 'none',
                  color: 'dodgerblue',
                  whiteSpace: 'nowrap'
                }}
                to={`/orders/${invoice.pkRegCart}`}
                weight={500}
              >
                View order # {invoice.pkRegCart}
              </AppText>
            )}
          </AppStack>
        </AppFlexbox>
      </Table.Td>

      <Table.Td style={CELL_STYLE}>
        <AppText style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
          {dayjs(formatUtcDate(invoice.createdAt)).format('MMM D, YYYY')}
        </AppText>
      </Table.Td>

      <Table.Td style={AMOUNT_CELL_STYLE}>
        <AppStack style={{ gap: 0, alignItems: 'end' }}>
          {summary.isPartiallySettled ? (
            <>
              <AppText style={STRUCK_AMOUNT_STYLE}>
                {currencyFormat(invoice.amountInCents / 100)}
              </AppText>
              <AppText style={DUE_AMOUNT_STYLE} weight={500}>
                {currencyFormat(invoice.balanceInCents / 100)} due
              </AppText>
            </>
          ) : (
            <AppText
              style={{ fontSize: 14, whiteSpace: 'nowrap' }}
              weight={500}
            >
              {currencyFormat(invoice.amountInCents / 100)}
            </AppText>
          )}
          {summary.hasRefund && (
            <AppText style={SUB_AMOUNT_STYLE}>
              {currencyFormat(invoice.amountRefundedInCents / 100)} refunded
            </AppText>
          )}
        </AppStack>
      </Table.Td>

      <Table.Td style={CELL_STYLE}>
        <Badge color={summary.statusColor} variant="light">
          {summary.statusLabel}
        </Badge>
      </Table.Td>

      <Table.Td style={AMOUNT_CELL_STYLE}>
        <AppFlexbox
          style={{ gap: 8, justifyContent: 'flex-end', flexWrap: 'nowrap' }}
        >
          <Button
            color="dark"
            onClick={() => onViewPayments(invoice)}
            size="compact-sm"
            variant="outline"
          >
            View payments
          </Button>
          {invoice.isPayable && (
            <Button
              color="blue"
              component={Link}
              size="compact-sm"
              to={`/checkout/invoices/${invoice.stripeInvoiceId}`}
            >
              Pay now
            </Button>
          )}
        </AppFlexbox>
      </Table.Td>
    </Table.Tr>
  ) : (
    <Table.Tr>
      <Table.Td style={CELL_STYLE}>
        <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
          <Skeleton height={28} width={28} />
          <AppStack style={{ gap: 5 }}>
            <Skeleton height={12} width={140} />
            <Skeleton height={10} width={100} />
          </AppStack>
        </AppFlexbox>
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={12} width={90} />
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={12} width={60} />
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={20} width={80} />
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={26} width={110} />
      </Table.Td>
    </Table.Tr>
  );
};

CustomerInvoiceListItem.propTypes = {
  invoice: PropTypes.object,
  onViewPayments: PropTypes.func
};

export default CustomerInvoiceListItem;
